/* eslint-disable import/no-unresolved */
import { Client } from "@modelcontextprotocol/sdk/client";
import { StreamableHTTPClientTransport } from "@modelcontextprotocol/sdk/client/streamableHttp";
/* eslint-enable import/no-unresolved */
import { DynamicStructuredTool } from "@langchain/core/tools";
import { authEvents, getValidAccessToken } from "services/auth";
import { z } from 'zod';

type McpSession = { client: Client; token: string };
type PendingSession = { token: string; promise: Promise<Client> };

let current: McpSession | null = null;
let pending: PendingSession | null = null;
// Bumped on every drop so a connect that started before an `unauthenticated` cannot resurrect the singleton.
let epoch = 0;

const noop = () => undefined;

const connectClient = async (token: string): Promise<Client> => {
  const mcpUrl = import.meta.env.VITE_MCP_URL;

  if (!mcpUrl) {
    throw new Error("VITE_MCP_URL is not defined");
  }

  const transport = new StreamableHTTPClientTransport(
    new URL(mcpUrl),
    {
      requestInit: {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
      reconnectionOptions: {
        initialReconnectionDelay: 1000,
        maxReconnectionDelay: 30000,
        reconnectionDelayGrowFactor: 1.5,
        maxRetries: 0,
      },
    }
  );

  const client = new Client({ name: "title-web-app", version: "1.0.0" });
  await client.connect(transport);
  return client;
};

const dropMcpClient = async (): Promise<void> => {
  epoch += 1;
  pending = null;
  const previous = current;
  current = null;
  if (previous) await previous.client.close();
};

const buildMcpClient = async (token: string): Promise<Client> => {
  const startedAt = epoch;
  if (current) {
    const previous = current;
    current = null;
    await previous.client.close().catch(noop);
  }
  const client = await connectClient(token);
  if (epoch !== startedAt || pending?.token !== token) {
    // Dropped or superseded while connecting: never publish this client.
    await client.close().catch(noop);
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    return getOrCreateMcpClient();
  }
  current = { client, token };
  pending = null;
  return client;
};

const getOrCreateMcpClient = async (): Promise<Client> => {
  const token = await getValidAccessToken();

  if (current?.token === token) return current.client;
  if (pending?.token === token) return pending.promise;

  const promise = buildMcpClient(token);
  pending = { token, promise };
  promise.catch(() => {
    if (pending?.promise === promise) pending = null;
  });
  return promise;
};

// A logout / refresh failure must not leave a client authenticated with the old session.
authEvents.on('unauthenticated', () => {
  dropMcpClient().catch(noop);
});

function jsonSchemaToZod(schema: any): z.ZodObject<any> {
  const properties = schema?.properties || {};
  const required: string[] = schema?.required || [];
  const shape: Record<string, z.ZodTypeAny> = {};
  // eslint-disable-next-line no-restricted-syntax
  for (const [key, prop] of Object.entries(properties)) {
    const p = prop as any;
    let field: z.ZodTypeAny;
    switch (p.type) {
      case 'number':
      case 'integer':
        field = z.number();
        break;
      case 'boolean':
        field = z.boolean();
        break;
      case 'array':
        field = z.array(z.any());
        break;
      case 'object':
        field = z.record(z.string(), z.any());
        break;
      default:
        field = z.string();
    }
    if (p.description) {
      field = field.describe(p.description);
    }
    if (!required.includes(key)) {
      field = field.optional();
    }
    shape[key] = field;
  }
  return z.object(shape);
}

export const getMcpTools = async (): Promise<DynamicStructuredTool[]> => {
  const client = await getOrCreateMcpClient();
  const { tools } = await client.listTools();

  const langchainTools = tools.map(
    (tool) =>
      new DynamicStructuredTool({
        name: tool.name,
        description: tool.description || "",
        schema: jsonSchemaToZod(tool.inputSchema),
        func: async (args) => {
          const activeClient = await getOrCreateMcpClient();
          const result = await activeClient.callTool({
            name: tool.name,
            arguments: args as any,
          });
          return JSON.stringify(result.content);
        },
      })
  );

  return langchainTools;
};

export const disconnectMcpClient = (): Promise<void> => dropMcpClient();
